import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaFlagUsa, FaMapMarkerAlt } from 'react-icons/fa';
import Breadcrumb from '../components/Breadcrumb.jsx';
import KeywordTopics from '../components/KeywordTopics.jsx';
import { setPageSEO } from '../utils/seo.js';
import { getSiblingCitiesForService } from '../utils/locationSolutions.js';
import { getKeywordCluster } from '../utils/keywordClusters.js';
import './LocationSolutionPage.css';

const reveal = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } }
};

const localProgramSteps = [
  ['Map buyer questions', 'List the trust questions customers ask before contacting, booking, or buying from a local business.'],
  ['Collect after real outcomes', 'Trigger requests after delivery, onboarding, support, renewal, or another moment customers can describe clearly.'],
  ['Organize by service and area', 'Tag approved proof by service, customer type, outcome, and location so the right story is easy to find.'],
  ['Publish and refresh', 'Place relevant stories on local landing pages and campaigns, then review coverage as offers and markets change.']
];

function LocationSolutionPage({ page }) {
  useEffect(() => {
    if (!page) return undefined;

    return setPageSEO({
      title: `${page.serviceName} in ${page.cityName} | Clientalio`,
      description: `${page.summary} Built for businesses in ${page.cityName}, ${page.state}.`,
      keywords: getKeywordCluster(page.slug),
      canonical: `https://clientalio.com/solutions/${page.slug}`,
      ogTitle: `${page.serviceName} in ${page.cityName} | Clientalio`,
      ogDescription: page.summary,
      twitterTitle: `${page.serviceName} in ${page.cityName} | Clientalio`,
      twitterDescription: page.summary
    });
  }, [page]);

  if (!page) {
    return (
      <main className="location-page">
        <div className="container">
          <Breadcrumb currentLabel="Solutions" />
          <h1>Solution not found</h1>
          <p>We couldn't find that location solution. <Link to="/">Return home</Link>.</p>
        </div>
      </main>
    );
  }

  const siblingCities = getSiblingCitiesForService(page.servicePrefix, page.citySlug);

  return (
    <main className="location-page">
      <div className="location-breadcrumb">
        <div className="container">
          <Breadcrumb
            items={[{ label: 'Solutions', to: '/solutions' }]}
            currentLabel={`${page.serviceName} – ${page.cityName}`}
          />
        </div>
      </div>

      <section className="location-hero">
        <div className="container location-hero-grid">
          <motion.div initial="hidden" animate="visible" variants={reveal}>
            <span className="location-eyebrow">{page.eyebrow} · {page.cityName.toUpperCase()}, {page.state}</span>
            <h1>{page.serviceName} for businesses in {page.cityName}</h1>
            <p>{page.summary}</p>
            <div className="location-hero-actions">
              <a className="btn-primary" href="/signup">Start collecting for free <FaArrowRight /></a>
              <Link to="/solutions" className="location-secondary-link">Explore all solutions</Link>
            </div>
            <p className="location-trust">No credit card required · Set up in minutes · No coding needed</p>
          </motion.div>
          <motion.aside initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55 }} className="location-hero-card">
            <FaMapMarkerAlt />
            <span>SERVING {page.cityName.toUpperCase()}</span>
            <ul>{page.benefits.map((benefit) => <li key={benefit}><FaCheckCircle /> {benefit}</li>)}</ul>
          </motion.aside>
        </div>
      </section>

      <KeywordTopics slug={page.slug} />

      <section className="location-workflow">
        <div className="container location-workflow-grid">
          <div>
            <span className="location-eyebrow">A PRACTICAL WORKFLOW</span>
            <h2>From customer response to published proof</h2>
            <p>Clientalio keeps collection, review, and publishing connected, so your strongest local customer stories never get lost.</p>
          </div>
          <div className="location-steps">
            <article><span>1</span><div><h3>Collect</h3><p>Share a simple request and let {page.cityName} customers respond from any device.</p></div></article>
            <article><span>2</span><div><h3>Review</h3><p>Choose the strongest responses and approve exactly what gets published.</p></div></article>
            <article><span>3</span><div><h3>Showcase</h3><p>Use polished customer proof on the pages and campaigns local buyers see first.</p></div></article>
          </div>
        </div>
      </section>

      <section className="location-use-cases">
        <div className="container">
          <div className="location-heading"><span>USE IT WHERE IT MATTERS</span><h2>Built for how {page.cityName} businesses win customers</h2></div>
          <div className="location-use-grid">
            {page.useCases.map((useCase) => (
              <article key={useCase}><FaCheckCircle /><h3>{useCase}</h3><p>Put relevant, local customer proof near the decisions your buyers are already making.</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className="location-workflow">
        <div className="container">
          <div className="location-heading"><span>LOCAL PROOF STRATEGY</span><h2>Build a proof library your {page.cityName} team can keep using</h2></div>
          <div className="location-use-grid">
            {localProgramSteps.map(([title, description]) => <article key={title}><FaCheckCircle /><h3>{title}</h3><p>{description}</p></article>)}
          </div>
        </div>
      </section>

      <section className="location-cta">
        <div className="container">
          <h2>Ready to bring {page.serviceName.toLowerCase()} to your {page.cityName} business?</h2>
          <p>Create your free Clientalio account and publish your first customer proof without waiting on a developer.</p>
          <a className="btn-primary" href="/signup">Start free today</a>
        </div>
      </section>

      {siblingCities.length > 0 && (
        <section className="location-also-support">
          <div className="container location-also-support-inner">
            <span className="location-also-support-flag"><FaFlagUsa /></span>
            <span className="location-also-support-text">We also support</span>
            <span className="location-also-support-flag"><FaFlagUsa /></span>
            <ul className="location-city-list">
              {siblingCities.map((sibling) => (
                <li key={sibling.slug}>
                  <Link to={`/solutions/${sibling.slug}`}>{sibling.cityName}</Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </main>
  );
}

export default LocationSolutionPage;
